import {
  ArrowTopRightOnSquareIcon,
  CalculatorIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  InformationCircleIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/20/solid";
import {useEffect, useMemo, useRef, useState} from "react";
import {AdminTabs} from "../components/AdminTabs";
import {CostCompositionBar} from "./CostCompositionBar";
import {Dialog, DialogBody, DialogTitle} from "catalyst";
import {OtsActionButton} from "../components/OtsButton";
import {OtsThemeRoot} from "../theme/OtsTheme";
import {TicketBillingLocationDetails, formatTicketBillingAfe} from "./TicketBillingLocationDetails";
import {TicketCostChargeList} from "./TicketCostChargeList";
import {TicketCostInputList} from "./TicketCostInputList";
import type {TicketCostInvoice, TicketCostRateModel, TicketCostVersionKind} from "./ticket-cost-types";
import {formatTicketCostCurrency} from "./ticket-cost-types";
import {WellCostSplitList} from "./WellCostSplitList";

export type TicketCostPanelProps = {
  rateModels: TicketCostRateModel[];
  initialRateModelId?: string;
  initialRateModelKey?: string;
  initialVersionKind?: TicketCostVersionKind;
  ticketLabel?: string;
  onEditCostInputs?: (rateModel: TicketCostRateModel) => void;
  onViewAllocation?: (rateModel: TicketCostRateModel) => void;
  onViewInvoice?: (invoice: TicketCostInvoice) => void;
};

type TicketCostPanelVersion = TicketCostRateModel & {noCurrentCost?: boolean};

const invoiceStatusClasses: Record<TicketCostInvoice["status"], string> = {
  draft: "text-[var(--ots-text-muted)]",
  approved: "text-[var(--ots-primary)]",
  sent: "text-[var(--ots-warning)]",
  paid: "text-[var(--ots-success)]",
  void: "text-[var(--ots-error)]",
};

const SectionSummary = ({children, count}: {children: string; count?: number | string}) => (
  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-sm font-semibold text-[var(--ots-secondary)] marker:content-none focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--ots-primary)]">
    <span className="inline-flex items-center gap-2">
      {children}
      {count !== undefined && <span className="text-xs font-normal text-[var(--ots-text-muted)]">({count})</span>}
    </span>
    <ChevronDownIcon className="size-4 text-[var(--ots-text-muted)] transition-transform group-open:rotate-180"/>
  </summary>
);

/**
 * Proposed compact ticket-cost surface. The component consumes an intentionally small view model so
 * transport DTOs can evolve independently from how calculated costs are explained.
 */
export const TicketCostPanel = ({
  rateModels,
  initialRateModelId,
  initialRateModelKey,
  initialVersionKind,
  ticketLabel = "Ticket cost",
  onEditCostInputs,
  onViewAllocation,
  onViewInvoice,
}: TicketCostPanelProps) => {
  const rateModelGroups = useMemo(() => {
    const groups = new Map<string, {key: string; label: string; versions: TicketCostPanelVersion[]}>();
    rateModels.forEach((candidate) => {
      const key = candidate.rateModelKey ?? candidate.name;
      const existing = groups.get(key);
      if (existing) existing.versions.push(candidate);
      else groups.set(key, {key, label: candidate.rateModelLabel ?? candidate.name, versions: [candidate]});
    });
    return Array.from(groups.values()).map((group) => {
      if (group.versions.every((version) => version.versionKind === "historical")) {
        group.versions.unshift({
          id: `${group.key}:no-current`,
          name: group.label,
          rateModelKey: group.key,
          versionKind: "current",
          versionLabel: "Current",
          versionOptionLabel: "Current",
          noCurrentCost: true,
          total: 0,
          buckets: [],
          lines: [],
        });
      }
      return group;
    });
  }, [rateModels]);
  const exactInitialModel = rateModels.find((candidate) => candidate.id === initialRateModelId);
  const initialGroupKey = exactInitialModel?.rateModelKey ?? exactInitialModel?.name ?? initialRateModelKey ?? rateModelGroups[0]?.key ?? "";
  const initialGroup = rateModelGroups.find((group) => group.key === initialGroupKey) ?? rateModelGroups[0];
  const initialModel = exactInitialModel
    ?? initialGroup?.versions.find((candidate) => candidate.versionKind === initialVersionKind)
    ?? initialGroup?.versions[0];
  const [activeRateModelKey, setActiveRateModelKey] = useState(initialGroupKey);
  const [selectedVersionIds, setSelectedVersionIds] = useState<Record<string, string>>(() => {
    const selections = Object.fromEntries(rateModelGroups.map((group) => [group.key, group.versions[0]?.id ?? ""]));
    if (initialModel) selections[initialGroupKey] = initialModel.id;
    return selections;
  });
  const [billingDetailsOpen, setBillingDetailsOpen] = useState(false);
  const manuallySelectedVersionKeys = useRef(new Set<string>());

  useEffect(() => {
    if (!rateModelGroups.length) return;

    setActiveRateModelKey((current) => rateModelGroups.some((group) => group.key === current) ? current : initialGroupKey);
    setSelectedVersionIds((current) => {
      const next = {...current};
      rateModelGroups.forEach((group) => {
        const currentSelectionExists = group.versions.some((candidate) => candidate.id === current[group.key]);
        const preferred = group.versions.find((candidate) => candidate.versionKind === initialVersionKind);
        if (!manuallySelectedVersionKeys.current.has(group.key) && preferred) next[group.key] = preferred.id;
        else if (!currentSelectionExists) next[group.key] = preferred?.id ?? group.versions[0]?.id ?? "";
      });
      return next;
    });
  }, [initialGroupKey, initialVersionKind, rateModelGroups]);
  const activeRateModel = rateModelGroups.find((group) => group.key === activeRateModelKey) ?? rateModelGroups[0];
  const selectedVersionId = activeRateModel ? selectedVersionIds[activeRateModel.key] : undefined;
  const model = useMemo(
    () => activeRateModel?.versions.find((candidate) => candidate.id === selectedVersionId) ?? activeRateModel?.versions[0],
    [activeRateModel, selectedVersionId],
  );

  if (!model) {
    return (
      <section className="rounded-xl border border-dashed border-[var(--ots-outline)] bg-[var(--ots-surface)] px-5 py-8 text-center">
        <CalculatorIcon className="mx-auto size-6 text-[var(--ots-text-muted)]"/>
        <h2 className="mt-2 font-semibold text-[var(--ots-secondary)]">No current cost</h2>
      </section>
    );
  }

  const currency = model.currency ?? "USD";
  const invoiceTotal = model.invoices?.reduce((sum, invoice) => invoice.status === "void" ? sum : sum + invoice.amount, 0) ?? 0;
  const previewDelta = model.preview?.comparisonTotal === undefined ? undefined : model.total - model.preview.comparisonTotal;
  const billingAfe = model.billingLocationDetails ? formatTicketBillingAfe(model.billingLocationDetails) : "";
  const populatedCostInputs = model.costInputs?.filter((input) => input.value).length ?? 0;
  const hasCurrentVersion = activeRateModel?.versions.some((candidate) => candidate.versionKind === "current" && !candidate.noCurrentCost) ?? false;

  return (
    <section aria-labelledby="ticket-cost-heading" className="overflow-hidden rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] shadow-sm">
      {rateModelGroups.length > 1 && (
        <AdminTabs<string>
          tabs={rateModelGroups.map((group) => ({id: group.key, label: group.label}))}
          activeTab={activeRateModel?.key ?? ""}
          onChange={setActiveRateModelKey}
          ariaLabel="Rate model"
          idPrefix="ticket-cost-rate-model"
          density="compact"/>
      )}
      <div
        id={rateModelGroups.length > 1 ? `ticket-cost-rate-model-${activeRateModel?.key}-panel` : undefined}
        role={rateModelGroups.length > 1 ? "tabpanel" : undefined}
        aria-labelledby={rateModelGroups.length > 1 ? `ticket-cost-rate-model-${activeRateModel?.key}-tab` : undefined}>
      <header className="px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="ticket-cost-heading" className="font-[family-name:var(--ots-font-display)] text-base font-semibold text-[var(--ots-secondary)]">{ticketLabel}</h2>
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <strong className="font-[family-name:var(--ots-font-display)] text-2xl font-semibold tabular-nums tracking-tight text-[var(--ots-secondary)]">
                {model.noCurrentCost ? "No current cost" : formatTicketCostCurrency(model.total, currency)}
              </strong>
              {!model.noCurrentCost && <span className="text-xs text-[var(--ots-text-muted)]">{model.financialLabel ?? "Total cost"}</span>}
            </div>
          </div>

          {(activeRateModel?.versions.length ?? 0) > 1 ? (
            <label className="block min-w-52 text-[10px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
              Version
              <select
                value={model.id}
                onChange={(event) => {
                  if (!activeRateModel) return;
                  manuallySelectedVersionKeys.current.add(activeRateModel.key);
                  setSelectedVersionIds((current) => ({...current, [activeRateModel.key]: event.target.value}));
                }}
                className="mt-1 block min-h-9 w-full rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2.5 py-1.5 text-xs font-medium normal-case tracking-normal text-[var(--ots-text)] focus:border-[var(--ots-primary)] focus:outline-none">
                {activeRateModel?.versions.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.versionOptionLabel ?? candidate.versionLabel ?? candidate.name}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--ots-text-muted)]">
          <span className="font-medium text-[var(--ots-text)]">{model.name}</span>
          {model.company && <><span aria-hidden="true">·</span><span>{model.company}</span></>}
          {model.modelType && <><span aria-hidden="true">·</span><span>{model.modelType}</span></>}
          {model.versionLabel && <><span aria-hidden="true">·</span><span>{model.versionLabel}</span></>}
          {model.calculatedAt && <><span aria-hidden="true">·</span><span className="inline-flex items-center gap-1"><ClockIcon className="size-3"/>{model.preview ? "Estimated" : "Calculated"} {model.calculatedAt}</span></>}
        </div>

        {model.historical && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-[var(--ots-warning-container)] px-3 py-2 text-xs text-[var(--ots-on-warning-container)]">
            <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0"/>
            This is a saved historical calculation.{hasCurrentVersion && " Select the current model to compare today's resolved rates."}
          </div>
        )}

        {model.preview && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-[var(--ots-primary-container)] px-3 py-2 text-xs text-[var(--ots-primary)]">
            <CalculatorIcon className="mt-0.5 size-4 shrink-0"/>
            <div className="min-w-0">
              <p>
                This is a preview calculation using current unposted ticket data. {hasCurrentVersion
                  ? "Select the current model to compare its posted costs."
                  : "No current posted calculation is available for comparison."}
              </p>
              {model.preview.updatedAt && <p className="mt-0.5 text-[11px] opacity-80">Estimated as of {model.preview.updatedAt}</p>}
            </div>
          </div>
        )}

        {!model.noCurrentCost && <>
        <div className="mt-4">
          <CostCompositionBar buckets={model.buckets} total={model.total} currency={currency} compact/>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--ots-outline-variant)] pt-3 text-[11px] text-[var(--ots-text-muted)]">
          {previewDelta !== undefined && (
            <span title={`Compared with ${formatTicketCostCurrency(model.preview?.comparisonTotal ?? 0, currency)}`}>
              <strong className="font-semibold tabular-nums text-[var(--ots-text)]">
                {previewDelta < 0 ? "−" : previewDelta > 0 ? "+" : ""}{formatTicketCostCurrency(Math.abs(previewDelta), currency)}
              </strong> vs {model.preview?.comparisonLabel ?? "posted"}
            </span>
          )}
          {model.billingLocationDetails ? (
            <span className="ml-auto inline-flex min-w-0 items-center gap-1" title={model.billingLocationDetails.resolution}>
              <MapPinIcon className="size-3.5 shrink-0"/>
              <span className="truncate">
                Bill at {model.billingLocationDetails.name}
                {model.billingLocationDetails.type && <> · {model.billingLocationDetails.type}</>}
                {billingAfe && <> · Expenditure code {billingAfe}</>}
                {model.billingLocationDetails.afeSourceLabel && <> · {model.billingLocationDetails.afeSourceLabel}</>}
              </span>
              <OtsActionButton variant="text" size="mini" aria-label="Explain billing location" title="Why this billing location?" onClick={() => setBillingDetailsOpen(true)}>
                <InformationCircleIcon className="size-4" aria-hidden="true"/>
              </OtsActionButton>
            </span>
          ) : model.billingLocation ? (
            <span className="ml-auto inline-flex min-w-0 items-center gap-1" title={model.billingLocationResolution}>
              <MapPinIcon className="size-3.5 shrink-0"/><span className="truncate">Bill at {model.billingLocation}</span>
            </span>
          ) : null}
        </div>
        </>}
      </header>

      {!model.noCurrentCost && <div className="space-y-2 border-t border-[var(--ots-outline-variant)] bg-[var(--ots-background)] p-2">
        <details open className="group overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]">
          <SectionSummary count={model.lines.length}>Charges</SectionSummary>
          <TicketCostChargeList lines={model.lines} currency={currency} expectedTotal={model.total}/>
        </details>

        {model.costInputs?.length ? (
          <details className="group overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]">
            <SectionSummary count={`${populatedCostInputs}/${model.costInputs.length}`}>Cost inputs</SectionSummary>
            <div className="border-t border-[var(--ots-outline-variant)] px-4 py-3">
              <TicketCostInputList inputs={model.costInputs} onEdit={onEditCostInputs ? () => onEditCostInputs(model) : undefined}/>
            </div>
          </details>
        ) : null}

        {model.allocations?.length ? (
          <details className="group overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]">
            <SectionSummary count={model.allocations.length}>Well allocation</SectionSummary>
            <div className="border-t border-[var(--ots-outline-variant)] px-4 py-3">
              <WellCostSplitList allocations={model.allocations} lines={model.lines} currency={currency} onViewAllocation={onViewAllocation ? () => onViewAllocation(model) : undefined}/>
            </div>
          </details>
        ) : null}

        {model.invoices?.length ? (
          <details className="group overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]">
            <SectionSummary count={model.invoices.length}>Invoices</SectionSummary>
            <div className="border-t border-[var(--ots-outline-variant)]">
              {model.invoices.map((invoice) => (
                <div key={invoice.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--ots-outline-variant)] px-4 py-2.5 text-xs last:border-b-0">
                  <ReceiptPercentIcon className="size-4 text-[var(--ots-text-muted)]"/>
                  <strong className="text-[var(--ots-text)]">{invoice.number}</strong>
                  <span className={["text-[10px] font-semibold capitalize", invoiceStatusClasses[invoice.status]].join(" ")}>{invoice.status}</span>
                  {invoice.invoiceDate && <span className="text-[var(--ots-text-muted)]">{invoice.invoiceDate}</span>}
                  <span className="ml-auto font-semibold tabular-nums text-[var(--ots-secondary)]">{formatTicketCostCurrency(invoice.amount, currency)}</span>
                  {onViewInvoice && (
                    <button type="button" onClick={() => onViewInvoice(invoice)} className="inline-flex items-center gap-1 font-semibold text-[var(--ots-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]">
                      Open <ArrowTopRightOnSquareIcon className="size-3.5"/>
                    </button>
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-3 bg-[var(--ots-background)] px-4 py-2 text-xs">
                <span className="text-[var(--ots-text-muted)]">Active invoice total</span>
                <strong className="tabular-nums text-[var(--ots-secondary)]">{formatTicketCostCurrency(invoiceTotal, currency)}</strong>
              </div>
            </div>
          </details>
        ) : null}
      </div>}
      </div>
      {model.billingLocationDetails && <Dialog open={billingDetailsOpen} onClose={() => setBillingDetailsOpen(false)} size="5xl" className="max-h-[calc(100vh-2rem)] overflow-y-auto !bg-transparent !p-0">
        <OtsThemeRoot colorScheme="inherit" className="rounded-2xl bg-[var(--ots-surface)] p-6">
          <div className="flex items-start justify-between gap-4">
            <DialogTitle className="!text-[var(--ots-text)]">Billing location decisions</DialogTitle>
            <OtsActionButton variant="text" size="compact" onClick={() => setBillingDetailsOpen(false)}>Close</OtsActionButton>
          </div>
          <DialogBody><TicketBillingLocationDetails location={model.billingLocationDetails}/></DialogBody>
        </OtsThemeRoot>
      </Dialog>}
    </section>
  );
};
